import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePark } from '../contexts/ParkContext';
import { 
  Plus, 
  Trash2, 
  Users as UsersIcon,
  UserPlus,
  Link as LinkIcon,
  Crown,
  Shield,
  UserCog,
  Eye,
  Building2,
  ToggleLeft,
  ToggleRight,
  Edit,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role configuration
const ROLE_CONFIG = {
  propietario: { label: 'Propietario', icon: Crown, color: 'bg-amber-500 text-white', badge: 'bg-amber-100 text-amber-800' },
  master: { label: 'Master', icon: Shield, color: 'bg-blue-600 text-white', badge: 'bg-blue-100 text-blue-800' },
  admin: { label: 'Administrador', icon: UserCog, color: 'bg-slate-700 text-white', badge: 'bg-slate-100 text-slate-800' },
  consulta: { label: 'Consulta', icon: Eye, color: 'bg-emerald-500 text-white', badge: 'bg-emerald-100 text-emerald-800' }
};

const Usuarios = () => {
  const { user: currentUser, isPropietario, isMaster, canManageUsers } = useAuth();
  const { parks, selectedPark } = usePark();
  const [users, setUsers] = useState([]);
  const [lots, setLots] = useState([]);
  const [lotUsers, setLotUsers] = useState([]);
  const [parkUsers, setParkUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAssignLotModal, setShowAssignLotModal] = useState(false);
  const [showAssignParkModal, setShowAssignParkModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null, userName: '' });

  const [userForm, setUserForm] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'consulta'
  });

  const [assignLotForm, setAssignLotForm] = useState({ user_id: '', lot_id: '' });
  const [assignParkForm, setAssignParkForm] = useState({ user_id: '', park_id: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        axios.get(`${API}/users`),
        axios.get(`${API}/lot-users`)
      ];
      
      if (selectedPark) {
        promises.push(axios.get(`${API}/lots?park_id=${selectedPark.id}`));
      }
      
      if (isMaster() || isPropietario()) {
        promises.push(axios.get(`${API}/park-users`));
      }
      
      const results = await Promise.all(promises);
      setUsers(results[0].data);
      setLotUsers(results[1].data);
      if (selectedPark) setLots(results[2]?.data || []);
      if (results[3]) setParkUsers(results[3].data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [selectedPark, isMaster, isPropietario]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, userForm);
      toast.success('Usuario creado exitosamente');
      setShowUserModal(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el usuario');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}/toggle-active`);
      toast.success(currentStatus ? 'Usuario desactivado' : 'Usuario activado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado');
      setDeleteConfirm({ open: false, userId: null, userName: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el usuario');
    }
  };

  const handleAssignLot = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lot-users`, assignLotForm);
      toast.success('Lote asignado al usuario');
      setShowAssignLotModal(false);
      setAssignLotForm({ user_id: '', lot_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar el lote');
    }
  };

  const handleAssignPark = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/park-users`, assignParkForm);
      toast.success('Parque asignado al administrador');
      setShowAssignParkModal(false);
      setAssignParkForm({ user_id: '', park_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar el parque');
    }
  };

  const handleUnassignLot = async (luId) => {
    if (!window.confirm('¿Quitar acceso a este lote?')) return;
    try {
      await axios.delete(`${API}/lot-users/${luId}`);
      toast.success('Acceso removido');
      fetchData();
    } catch (error) {
      toast.error('Error al remover el acceso');
    }
  };

  const handleUnassignPark = async (puId) => {
    if (!window.confirm('¿Quitar acceso a este parque?')) return;
    try {
      await axios.delete(`${API}/park-users/${puId}`);
      toast.success('Acceso removido');
      fetchData();
    } catch (error) {
      toast.error('Error al remover el acceso');
    }
  };

  const resetUserForm = () => {
    setUserForm({ nombre: '', email: '', password: '', rol: getDefaultRole() });
    setEditingUser(null);
  };

  const getDefaultRole = () => {
    if (isPropietario()) return 'master';
    if (isMaster()) return 'admin';
    return 'consulta';
  };

  const getAvailableRoles = () => {
    if (isPropietario()) return ['master'];
    if (isMaster()) return ['admin', 'consulta'];
    return [];
  };

  const getUserLots = (userId) => {
    return lotUsers
      .filter(lu => lu.user_id === userId)
      .map(lu => {
        const lot = lots.find(l => l.id === lu.lot_id);
        return { ...lu, numero_lote: lot?.numero_lote };
      });
  };

  const getUserParks = (userId) => {
    return parkUsers
      .filter(pu => pu.user_id === userId)
      .map(pu => {
        const park = parks.find(p => p.id === pu.park_id);
        return { ...pu, nombre: park?.nombre };
      });
  };

  // Filter users based on role
  const masterUsers = users.filter(u => u.rol === 'master');
  const adminUsers = users.filter(u => u.rol === 'admin');
  const consultaUsers = users.filter(u => u.rol === 'consulta');

  const UserCard = ({ user, showActions = true }) => {
    const config = ROLE_CONFIG[user.rol];
    const Icon = config?.icon || Eye;
    const userParks = getUserParks(user.id);
    const userLots = getUserLots(user.id);
    
    return (
      <div className={`card-industrial ${!user.activo ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 flex items-center justify-center ${config?.color || 'bg-slate-500 text-white'}`}>
              <Icon size={20} />
            </div>
            <div>
              <p className="font-bold text-lg">{user.nombre}</p>
              <p className="text-sm text-slate-500">{user.email}</p>
            </div>
          </div>
          <span className={`px-2 py-1 text-xs font-medium ${config?.badge || 'bg-slate-100 text-slate-800'}`}>
            {config?.label || user.rol}
          </span>
        </div>
        
        {/* Assigned Parks (for admin users) */}
        {user.rol === 'admin' && (
          <div className="mb-3">
            <p className="text-xs text-slate-500 mb-1">Parques asignados:</p>
            <div className="flex flex-wrap gap-1">
              {userParks.length === 0 ? (
                <span className="text-slate-400 text-sm">Sin parques</span>
              ) : (
                userParks.map(up => (
                  <span key={up.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs">
                    <Building2 size={12} />
                    {up.nombre}
                    {showActions && (
                      <button onClick={() => handleUnassignPark(up.id)} className="hover:text-red-500">
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Assigned Lots (for consulta users) */}
        {user.rol === 'consulta' && (
          <div className="mb-3">
            <p className="text-xs text-slate-500 mb-1">Lotes asignados:</p>
            <div className="flex flex-wrap gap-1">
              {userLots.length === 0 ? (
                <span className="text-slate-400 text-sm">Sin lotes</span>
              ) : (
                userLots.map(ul => (
                  <span key={ul.id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs font-mono">
                    {ul.numero_lote}
                    {showActions && (
                      <button onClick={() => handleUnassignLot(ul.id)} className="hover:text-red-500">
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))
              )}
              {showActions && lots.length > 0 && (
                <button
                  onClick={() => {
                    setAssignLotForm({ ...assignLotForm, user_id: user.id });
                    setShowAssignLotModal(true);
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs hover:bg-blue-100 transition-colors"
                  title="Asignar otro lote"
                >
                  <Plus size={12} />
                  Agregar
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Status indicator */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className={`text-xs ${user.activo !== false ? 'text-emerald-600' : 'text-red-500'}`}>
            {user.activo !== false ? '● Activo' : '● Inactivo'}
          </span>
          
          {showActions && user.id !== currentUser?.id && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleToggleActive(user.id, user.activo !== false)}
                className="p-1.5 hover:bg-slate-100 transition-colors"
                title={user.activo !== false ? 'Desactivar' : 'Activar'}
              >
                {user.activo !== false ? (
                  <ToggleRight size={18} className="text-emerald-500" />
                ) : (
                  <ToggleLeft size={18} className="text-slate-400" />
                )}
              </button>
              <button
                onClick={() => {
                  console.log('Delete clicked for:', user.id, user.nombre);
                  setDeleteConfirm({ open: true, userId: user.id, userName: user.nombre });
                }}
                className="p-1.5 hover:bg-red-50 transition-colors"
                title="Eliminar"
              >
                <Trash2 size={16} className="text-red-500" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Propietario view: Only manage Masters
  if (isPropietario()) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1 data-testid="usuarios-title">
            <Crown className="inline mr-2 text-amber-500" size={28} />
            Gestión de Usuarios Master
          </h1>
          <p>Administra los usuarios que gestionan parques industriales</p>
        </div>

        <div className="action-bar mb-8">
          <span className="text-slate-500">{masterUsers.length} usuarios master</span>
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => { resetUserForm(); setShowUserModal(true); }}
            data-testid="add-master-btn"
          >
            <UserPlus size={16} className="mr-2" />
            Nuevo Master
          </Button>
        </div>

        {loading ? (
          <div className="card-industrial animate-pulse h-64"></div>
        ) : masterUsers.length === 0 ? (
          <div className="empty-state py-16 card-industrial">
            <Shield size={48} className="text-slate-300 mx-auto mb-4" />
            <h3>No hay usuarios master</h3>
            <p>Crea el primer usuario master para comenzar a gestionar parques.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {masterUsers.map(user => (
              <UserCard key={user.id} user={user} />
            ))}
          </div>
        )}

        {/* Create Master Modal */}
        <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold flex items-center gap-2">
                <Shield size={20} className="text-blue-600" />
                Nuevo Usuario Master
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="label-overline">Nombre</Label>
                  <Input
                    value={userForm.nombre}
                    onChange={(e) => setUserForm({ ...userForm, nombre: e.target.value })}
                    placeholder="Nombre completo"
                    required
                    className="input-industrial"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Email</Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    required
                    className="input-industrial"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Contraseña</Label>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="input-industrial"
                  />
                </div>
                <input type="hidden" value="master" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowUserModal(false)} className="rounded-none">
                  Cancelar
                </Button>
                <Button type="submit" className="btn-industrial bg-blue-600 text-white hover:bg-blue-700">
                  Crear Master
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Confirm Delete Dialog for Propietario */}
        <ConfirmDialog
          open={deleteConfirm.open}
          onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
          onConfirm={() => handleDeleteUser(deleteConfirm.userId)}
          title="¿Estás seguro que deseas eliminar?"
          description={`El usuario "${deleteConfirm.userName}" será eliminado permanentemente.`}
          confirmText="Eliminar"
        />
      </div>
    );
  }

  // Master view: Manage Admins and Consultas
  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="usuarios-title">
          <UsersIcon className="inline mr-2" size={28} />
          Gestión de Usuarios
        </h1>
        <p>Administra los usuarios de tus parques industriales</p>
      </div>

      <div className="action-bar mb-8">
        <span className="text-slate-500">
          {adminUsers.length} administradores, {consultaUsers.length} usuarios consulta
        </span>
        <div className="flex items-center gap-3">
          {consultaUsers.length > 0 && (
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setShowAssignLotModal(true)}
              disabled={!selectedPark || lots.length === 0}
              title={!selectedPark ? "Selecciona un parque primero" : lots.length === 0 ? "No hay lotes en este parque. Créalos en 'Directorio de Lotes'" : "Asignar lote a usuario"}
              data-testid="assign-lot-btn"
            >
              <LinkIcon size={16} className="mr-2" />
              Asignar Lote
              {lots.length === 0 && selectedPark && (
                <span className="ml-1 text-xs text-amber-600">(Sin lotes)</span>
              )}
            </Button>
          )}
          {adminUsers.length > 0 && parks.length > 0 && (
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setShowAssignParkModal(true)}
              data-testid="assign-park-btn"
            >
              <Building2 size={16} className="mr-2" />
              Asignar Parque
            </Button>
          )}
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => { resetUserForm(); setShowUserModal(true); }}
            data-testid="add-user-btn"
          >
            <UserPlus size={16} className="mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="card-industrial animate-pulse h-64"></div>
      ) : (
        <Tabs defaultValue="admin" className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-none">
            <TabsTrigger value="admin" className="rounded-none data-[state=active]:bg-white">
              <UserCog size={16} className="mr-2" />
              Administradores ({adminUsers.length})
            </TabsTrigger>
            <TabsTrigger value="consulta" className="rounded-none data-[state=active]:bg-white">
              <Eye size={16} className="mr-2" />
              Consulta ({consultaUsers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="admin">
            {adminUsers.length === 0 ? (
              <div className="empty-state py-16 card-industrial">
                <UserCog size={48} className="text-slate-300 mx-auto mb-4" />
                <h3>No hay administradores</h3>
                <p>Crea un usuario administrador para gestionar la operación del parque.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {adminUsers.map(user => (
                  <UserCard key={user.id} user={user} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="consulta">
            {consultaUsers.length === 0 ? (
              <div className="empty-state py-16 card-industrial">
                <Eye size={48} className="text-slate-300 mx-auto mb-4" />
                <h3>No hay usuarios de consulta</h3>
                <p>Crea usuarios de consulta para propietarios o inquilinos de lotes.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {consultaUsers.map(user => (
                  <UserCard key={user.id} user={user} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Create User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Nombre</Label>
                <Input
                  value={userForm.nombre}
                  onChange={(e) => setUserForm({ ...userForm, nombre: e.target.value })}
                  placeholder="Nombre completo"
                  required
                  className="input-industrial"
                  data-testid="user-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                  className="input-industrial"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Contraseña</Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="input-industrial"
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Tipo de Usuario</Label>
                <Select
                  value={userForm.rol}
                  onValueChange={(value) => setUserForm({ ...userForm, rol: value })}
                >
                  <SelectTrigger className="input-industrial" data-testid="user-rol-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles().map(rol => (
                      <SelectItem key={rol} value={rol}>
                        {ROLE_CONFIG[rol]?.label || rol}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {userForm.rol === 'admin' && 'Puede gestionar toda la información operativa del parque'}
                  {userForm.rol === 'consulta' && 'Solo puede ver información de los lotes asignados'}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-user-btn">
                Crear Usuario
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Lot Modal */}
      <Dialog open={showAssignLotModal} onOpenChange={setShowAssignLotModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Asignar Lote a Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssignLot}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Usuario de Consulta</Label>
                <Select
                  value={assignLotForm.user_id}
                  onValueChange={(value) => setAssignLotForm({ ...assignLotForm, user_id: value })}
                >
                  <SelectTrigger className="input-industrial">
                    <SelectValue placeholder="Seleccionar usuario..." />
                  </SelectTrigger>
                  <SelectContent>
                    {consultaUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Lote ({selectedPark?.nombre})</Label>
                <Select
                  value={assignLotForm.lot_id}
                  onValueChange={(value) => setAssignLotForm({ ...assignLotForm, lot_id: value })}
                >
                  <SelectTrigger className="input-industrial">
                    <SelectValue placeholder="Seleccionar lote..." />
                  </SelectTrigger>
                  <SelectContent>
                    {lots.map(lot => (
                      <SelectItem key={lot.id} value={lot.id}>{lot.numero_lote}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignLotModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800">
                Asignar Lote
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Park Modal */}
      <Dialog open={showAssignParkModal} onOpenChange={setShowAssignParkModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Asignar Parque a Administrador</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssignPark}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Administrador</Label>
                <Select
                  value={assignParkForm.user_id}
                  onValueChange={(value) => setAssignParkForm({ ...assignParkForm, user_id: value })}
                >
                  <SelectTrigger className="input-industrial">
                    <SelectValue placeholder="Seleccionar administrador..." />
                  </SelectTrigger>
                  <SelectContent>
                    {adminUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Parque</Label>
                <Select
                  value={assignParkForm.park_id}
                  onValueChange={(value) => setAssignParkForm({ ...assignParkForm, park_id: value })}
                >
                  <SelectTrigger className="input-industrial">
                    <SelectValue placeholder="Seleccionar parque..." />
                  </SelectTrigger>
                  <SelectContent>
                    {parks.map(park => (
                      <SelectItem key={park.id} value={park.id}>{park.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignParkModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800">
                Asignar Parque
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
        onConfirm={() => handleDeleteUser(deleteConfirm.userId)}
        title="¿Estás seguro que deseas eliminar?"
        description={`El usuario "${deleteConfirm.userName}" será eliminado permanentemente.`}
        confirmText="Eliminar"
      />
    </div>
  );
};

export default Usuarios;
